import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    ViewChild,
} from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { SelectOption } from '@pf/shared-common';
import {
    InputComponent,
    LayoutModule,
    PlatformFormsModule,
    SelectComponent,
    SharedUiModule,
} from '@pf/shared-ui';
import { CpwPackageType, CpwPackingItem } from '@cpw/domain';
import { PackingListDto, PackLineDto } from '@pf/platform-wms/domain';
import { PackingStatus } from '@control-tower/platform-wms';
import { CpwQuantityInputComponent } from './cpw-quantity-input.component';
import { LoggerService } from '@pf/shared-services';

export type CpwPackingFormValue = {
    packLines: PackLineDto[];
    quantity?: number | null;
    selectedBox?: string | null;
    lengthIn?: number | null;
    widthIn?: number | null;
    heightIn?: number | null;
    weightLbs?: number | null;
};

export type CpwBoxType = {
    id: string;
    type: string;
    code: string;
    isCustomBox: boolean;
    lengthIn?: number | null;
    widthIn?: number | null;
    heightIn?: number | null;
    weightLbs?: number | null;
};

@Component({
    selector: 'cpw-packing-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        NzCheckboxModule,
        SharedUiModule,
        LayoutModule,
        PlatformFormsModule,
        CpwQuantityInputComponent,
    ],
    templateUrl: './cpw-packing-form.component.html',
    styleUrl: './cpw-packing-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwPackingFormComponent implements AfterViewInit {
    private readonly logger = inject(LoggerService);

    @ViewChild(SelectComponent) private boxSelect?: SelectComponent;
    @ViewChild('weightInput') private weightInput?: InputComponent;

    readonly style = input<'mobile' | 'desktop'>('mobile');
    readonly package = input<PackingListDto | null>(null);
    readonly disabled = input<boolean>(false);
    readonly quantityDefault = input<number | null>(null);
    readonly availableItems = input<CpwPackingItem[]>([]);
    readonly packageType = input<CpwPackageType>('box');

    valueChange = output<CpwPackingFormValue>();
    validChange = output<boolean>();

    boxTypes = input<CpwBoxType[]>([]);

    boxOptions = computed(() => {
        const options: SelectOption[] = [];

        options.push(
            ...this.boxTypes().map(type => ({
                value: type.code,
                label: `${type.type} (${type.code})`,
            }))
        );

        return options;
    });

    readonly isMobile = computed(() => {
        return this.style() === 'mobile';
    });

    readonly inputStyle = computed(() => {
        return this.style() === 'mobile' ? 'compact' : 'block';
    });

    readonly packageTypeOptions: SelectOption[] = [
        { value: 'box', label: 'Box' },
        { value: 'pallet', label: 'Pallet' },
    ];

    readonly itemRows = new FormArray<
        FormGroup<{
            checked: FormControl<boolean>;
            itemId: FormControl<string>;
            itemDescription: FormControl<string>;
            quantity: FormControl<number | null>;
            maxQuantity: FormControl<number>;
        }>
    >([]);

    readonly formGroup = new FormGroup({
        selectedBox: new FormControl<string | null>(null),
        quantity: new FormControl<number | null>(null, {
            nonNullable: false,
            validators: [Validators.required],
        }),
        lengthIn: new FormControl<number | null>(null, {
            nonNullable: false,
        }),
        widthIn: new FormControl<number | null>(null, {
            nonNullable: false,
        }),
        heightIn: new FormControl<number | null>(null, {
            nonNullable: false,
        }),
        weightLbs: new FormControl<number | null>(null, {
            nonNullable: false,
            validators: [Validators.required],
        }),
    });

    private readonly boxTypeRequiredValidator = Validators.required;

    readonly isPacked = computed(() => {
        return (
            this.package()?.packLines?.some(
                pl => pl.status === PackingStatus.Packed
            ) ?? false
        );
    });

    readonly allItemsChecked = computed(() => {
        const controls = this.itemRows.controls;
        if (controls.length === 0) {
            return false;
        }
        return controls.every(row => row.controls.checked.value);
    });

    readonly someItemsChecked = computed(() => {
        const controls = this.itemRows.controls;
        if (controls.length === 0) {
            return false;
        }
        return (
            controls.some(row => row.controls.checked.value) &&
            !this.allItemsChecked()
        );
    });

    private lastAvailableItemsKey: string | null = null;

    ngAfterViewInit(): void {
        this.focusBoxSelectIfEmpty();
    }

    constructor() {
        effect(() => {
            this.packageType();
            this.setBoxTypeRequired();
        });

        effect(() => {
            const initialPackage = this.package();
            if (initialPackage && this.formGroup.invalid) {
                // emitEvent: false — this patch mirrors values the parent just
                // pushed via [package]. Re-emitting them through valueChange
                // makes the parent rebuild the package (new ref), re-firing
                // this effect while the form is still invalid: an infinite
                // change-detection loop (NG0103).
                this.formGroup.patchValue(
                    {
                        selectedBox:
                            this.boxTypes().find(
                                t => t.id === initialPackage.item?.id
                            )?.code ?? null,
                        quantity:
                            initialPackage.packLines?.reduce(
                                (sum, pl) => sum + (pl.packedQuantity ?? 0),
                                0
                            ) ?? null,
                        lengthIn:
                            initialPackage.dimensions?.length?.inches ?? null,
                        widthIn:
                            initialPackage.dimensions?.width?.inches ?? null,
                        heightIn:
                            initialPackage.dimensions?.height?.inches ?? null,
                        weightLbs: initialPackage.weight?.pounds ?? null,
                    },
                    { emitEvent: false }
                );

                if (
                    initialPackage.packLines?.length &&
                    this.itemRows.length === 0
                ) {
                    this.buildItemRows(
                        initialPackage.packLines.map(pl => ({
                            itemId: pl.bundle?.item?.id as string,
                            itemDescription: pl.bundle?.item?.name ?? '',
                            quantityToPack: pl.packedQuantity ?? 0,
                            bundles: pl.bundle ? [pl.bundle] : [],
                        })),
                        initialPackage.packLines
                    );
                }

                // The silent patch above may have changed validity; tell the
                // parent directly since valueChanges did not fire.
                this.validChange.emit(this.formGroup.valid);
            }
        });

        effect(() => {
            const items = this.availableItems();
            const key = items
                .map(i => `${i.itemId}:${i.quantityToPack}`)
                .join(',');
            if (items.length > 0 && key !== this.lastAvailableItemsKey) {
                this.lastAvailableItemsKey = key;
                const pkg = this.package();
                this.buildItemRows(items, pkg?.packLines);
            }
        });

        effect(() => {
            const boxTypes = this.boxTypes();
            const currentPackage = this.package();
            const currentSelectedBox =
                this.formGroup.controls.selectedBox.value;

            if (
                boxTypes.length > 0 &&
                currentPackage?.item?.id &&
                !currentSelectedBox
            ) {
                const matchingBox = boxTypes.find(
                    t => t.id === currentPackage.item?.id
                );
                if (matchingBox) {
                    this.formGroup.patchValue({
                        selectedBox: matchingBox.code,
                    });
                }
            }
        });

        effect(() => {
            const defaultQuantity = this.quantityDefault();
            if (defaultQuantity === null || defaultQuantity === undefined) {
                return;
            }

            const current = this.formGroup.controls.quantity.value;
            if (current === null || current === undefined) {
                this.formGroup.controls.quantity.setValue(defaultQuantity);
            }
        });

        this.formGroup.valueChanges.subscribe(() => {
            this.emitValue();
            this.validChange.emit(this.formGroup.valid);
        });

        this.itemRows.valueChanges.subscribe(() => {
            this.syncQuantityFromItems();
            this.emitValue();
            this.validChange.emit(this.formGroup.valid);
        });
    }

    private setBoxTypeRequired() {
        const selectedBoxControl = this.formGroup.controls.selectedBox;
        if (this.packageType() === 'box') {
            selectedBoxControl.addValidators(this.boxTypeRequiredValidator);
        } else {
            selectedBoxControl.removeValidators(this.boxTypeRequiredValidator);
            if (selectedBoxControl.value) {
                selectedBoxControl.setValue(null, { emitEvent: false });
            }
        }
        selectedBoxControl.updateValueAndValidity({ emitEvent: false });
        this.validChange.emit(this.formGroup.valid);
    }

    get value(): CpwPackingFormValue | null {
        const packLines = this.getCheckedItems();

        return {
            packLines,
            quantity: this.formGroup.controls.quantity.value,
            selectedBox: this.formGroup.controls.selectedBox.value,
            lengthIn: this.formGroup.controls.lengthIn.value,
            widthIn: this.formGroup.controls.widthIn.value,
            heightIn: this.formGroup.controls.heightIn.value,
            weightLbs: this.formGroup.controls.weightLbs.value,
        };
    }

    get hasItems(): boolean {
        return this.itemRows.length > 0;
    }

    private focusBoxSelectIfEmpty() {
        if (
            this.disabled() ||
            this.isPacked() ||
            this.formGroup.controls.selectedBox.value
        ) {
            return;
        }
        setTimeout(() => {
            this.boxSelect?.focus();
        });
    }

    protected onBoxSelected(event$: SelectOption) {
        const boxType = this.boxTypes().find(t => t.code === event$.value);

        if (!boxType) {
            return;
        }

        if (!boxType.lengthIn || !boxType.widthIn || !boxType.heightIn) {
            return;
        }
        this.formGroup.controls.lengthIn.setValue(boxType.lengthIn);
        this.formGroup.controls.widthIn.setValue(boxType.widthIn);
        this.formGroup.controls.heightIn.setValue(boxType.heightIn);
    }

    protected onItemCheckedChange(index: number) {
        const row = this.itemRows.at(index);
        if (!row) {
            return;
        }

        if (row.controls.checked.value) {
            row.controls.quantity.enable();
            if (
                row.controls.quantity.value === null ||
                row.controls.quantity.value === 0
            ) {
                row.controls.quantity.setValue(row.controls.maxQuantity.value);
            }
        } else {
            row.controls.quantity.setValue(0);
            row.controls.quantity.disable();
        }

        this.focusWeightIfAllItemsChecked();
    }

    protected onToggleAll() {
        const shouldCheckAll = !this.allItemsChecked();

        this.itemRows.controls.forEach((row, index) => {
            row.controls.checked.setValue(shouldCheckAll);
            this.onItemCheckedChange(index);
        });
    }

    private focusWeightIfAllItemsChecked() {
        if (this.formGroup.controls.weightLbs.value) {
            return;
        }
        if (this.itemRows.controls.every(r => r.controls.checked.value)) {
            setTimeout(() => this.weightInput?.focus());
        }
    }

    private buildItemRows(
        items: CpwPackingItem[],
        existingPackLines?: PackLineDto[]
    ) {
        this.itemRows.clear();

        // Disable the entire form if any pack lines are packed
        if (this.isPacked()) {
            this.formGroup.disable();
        }

        for (const item of items) {
            // Sum up quantities from existing pack lines for this item
            const existingPackLinesForItem = existingPackLines?.filter(
                e => e.bundle?.item?.id === item.itemId
            );

            const totalPackedQuantity =
                existingPackLinesForItem?.reduce(
                    (sum, pl) => sum + (pl.packedQuantity ?? 0),
                    0
                ) ?? 0;

            // Only check items that are already in pack lines
            const checked = totalPackedQuantity > 0;
            const qty = checked ? totalPackedQuantity : 0;

            const row = new FormGroup({
                checked: new FormControl<boolean>(checked, {
                    nonNullable: true,
                }),
                itemId: new FormControl<string>(item.itemId, {
                    nonNullable: true,
                }),
                itemDescription: new FormControl<string>(item.itemDescription, {
                    nonNullable: true,
                }),
                quantity: new FormControl<number | null>(qty, {
                    nonNullable: false,
                }),
                maxQuantity: new FormControl<number>(item.quantityToPack, {
                    nonNullable: true,
                }),
            });

            if (!checked) {
                row.controls.quantity.disable();
            }

            this.itemRows.push(row);
        }

        this.syncQuantityFromItems();
        this.validChange.emit(this.formGroup.valid);
    }

    private getCheckedItems(): PackLineDto[] {
        const packLines: PackLineDto[] = [];
        const pkg = this.package();

        if (!pkg) {
            return [];
        }

        // If the package has any packed pack lines, return the exact existing pack lines
        if (this.isPacked()) {
            return pkg.packLines.filter(
                pl => pl.status === PackingStatus.Packed
            );
        }

        // Otherwise, create new pack lines as before
        for (const row of this.itemRows.controls) {
            if (!row.controls.checked.value) {
                continue;
            }

            const quantityToPack = row.controls.quantity.value ?? 0;
            if (quantityToPack <= 0) {
                continue;
            }

            // Find the item from availableItems to get its bundles
            const item = this.availableItems().find(
                item => item.itemId === row.controls.itemId.value
            );

            if (!item || !item.bundles || item.bundles.length === 0) {
                continue;
            }

            // Calculate total available quantity across all bundles
            const totalAvailableQuantity = this.getTotalAvailableQuantity(item);

            // Check if we have insufficient quantity
            if (quantityToPack > totalAvailableQuantity) {
                this.logger.warn(
                    `Insufficient bundle quantity for item ${item.itemDescription}. ` +
                        `Requested: ${quantityToPack}, Available: ${totalAvailableQuantity}`
                );
                // Still proceed but create pack lines for what's available
            }

            // Sort bundles by available quantity (use bundles with most available quantity first)
            const sortedBundles = [...item.bundles].sort(
                (a, b) =>
                    (b.availableQuantity ?? 0) - (a.availableQuantity ?? 0)
            );

            let remainingQuantity = quantityToPack;

            for (const bundle of sortedBundles) {
                if (remainingQuantity <= 0) {
                    break;
                }

                const availableQuantity = bundle.availableQuantity ?? 0;
                if (availableQuantity <= 0) {
                    continue;
                }

                const quantityFromThisBundle = Math.min(
                    remainingQuantity,
                    availableQuantity
                );

                packLines.push(
                    new PackLineDto({
                        bundle,
                        packedQuantity: quantityFromThisBundle,
                    })
                );

                remainingQuantity -= quantityFromThisBundle;
            }

            // If we couldn't allocate all requested quantity, create a pack line with the remaining quantity
            // This represents a shortfall that needs to be handled
            if (remainingQuantity > 0) {
                packLines.push(
                    new PackLineDto({
                        bundle: sortedBundles[0], // Use the first bundle as reference
                        packedQuantity: remainingQuantity,
                    })
                );
            }
        }

        return packLines;
    }

    private syncQuantityFromItems() {
        if (this.itemRows.length === 0) {
            return;
        }

        const total = this.getCheckedItems().reduce(
            (sum, item) => sum + (item.packedQuantity ?? 0),
            0
        );
        this.formGroup.controls.quantity.setValue(total, {
            emitEvent: false,
        });
    }

    getItemByItemId(itemId: string): CpwPackingItem {
        const item = this.availableItems().find(item => item.itemId === itemId);
        if (!item) {
            throw new Error(`Item with ID ${itemId} not found`);
        }
        return item;
    }

    getTotalAvailableQuantity(item: CpwPackingItem | undefined): number {
        if (!item?.bundles) {
            return 0;
        }
        return item.bundles.reduce(
            (sum, bundle) => sum + (bundle.availableQuantity ?? 0),
            0
        );
    }

    private emitValue() {
        const value = this.value;

        if (value) {
            this.valueChange.emit(value);
        }
    }

    protected readonly Math = Math;
}
